'use client'

import * as React from 'react'
import { cn } from '../../utils/cn'
import { Search, X, Check, Loader2, Users, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useVirtualizer } from '@tanstack/react-virtual'
import { BirhausInput } from '../BirhausInput'
import { BirhausButton } from '../BirhausButton'
import { BirhausEmptyState } from '../BirhausEmptyState'
import { BirhausBadge } from '../BirhausBadge'
import { BirhausCheckbox } from '../BirhausCheckbox'

export interface BirhausSearchableListProps<T> {
  // Core data props
  items: T[]
  getItemKey: (item: T) => string
  renderItem?: (item: T, isSelected: boolean) => React.ReactNode
  
  // Search configuration
  searchKeys?: (keyof T)[]
  searchPlaceholderEs?: string
  searchPlaceholderEn?: string
  debounceMs?: number
  
  // Selection props
  onSelect?: (item: T) => void
  onMultiSelect?: (items: T[]) => void
  multiSelect?: boolean
  selectedItems?: T[]
  maxSelectable?: number
  showSelectedCount?: boolean
  showSelectAll?: boolean
  
  // Display configuration
  height?: number | string
  virtualize?: boolean
  itemHeight?: number
  loading?: boolean
  
  // Grouping and sorting
  groupBy?: (item: T) => string
  sortBy?: (a: T, b: T) => number
  
  // Styling
  className?: string
  listClassName?: string
  searchClassName?: string
  
  // BIRHAUS-specific props
  emptyMessageEs?: string
  emptyMessageEn?: string
  emptySearchMessageEs?: string
  emptySearchMessageEn?: string
  showCognitiveWarning?: boolean
  cognitiveMode?: 'strict' | 'warning' | 'disabled'
  
  // Spanish-first labels
  selectAllLabelEs?: string
  selectAllLabelEn?: string
  selectedCountLabelEs?: string
  selectedCountLabelEn?: string
  maxSelectionLabelEs?: string
  maxSelectionLabelEn?: string
}

export function BirhausSearchableList<T>({
  items,
  getItemKey,
  renderItem,
  searchKeys = [],
  searchPlaceholderEs = 'Buscar...',
  searchPlaceholderEn = 'Search...',
  debounceMs = 300,
  onSelect,
  onMultiSelect,
  multiSelect = false,
  selectedItems = [],
  maxSelectable,
  showSelectedCount = true,
  showSelectAll = true,
  height = 400,
  virtualize = true,
  itemHeight = 60,
  loading = false,
  groupBy,
  sortBy,
  className,
  listClassName,
  searchClassName,
  emptyMessageEs = 'No hay elementos',
  emptyMessageEn = 'No items found',
  emptySearchMessageEs = 'No se encontraron resultados',
  emptySearchMessageEn = 'No search results found',
  showCognitiveWarning = false,
  cognitiveMode = 'warning',
  selectAllLabelEs = 'Seleccionar todos',
  selectAllLabelEn = 'Select all',
  selectedCountLabelEs = '{selected} de {total} seleccionados',
  selectedCountLabelEn = '{selected} of {total} selected',
  maxSelectionLabelEs = 'máx: {max}',
  maxSelectionLabelEn = 'max: {max}'
}: BirhausSearchableListProps<T>) {
  const [searchTerm, setSearchTerm] = React.useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState('')
  const [selectedKeys, setSelectedKeys] = React.useState<Set<string>>(
    new Set(selectedItems.map(getItemKey))
  )
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(new Set())
  
  const listRef = React.useRef<HTMLDivElement>(null)
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  // BIRHAUS: Cognitive load warnings
  const shouldShowCognitiveWarning = React.useMemo(() => {
    if (cognitiveMode === 'disabled') return false
    
    const hasHighItemCount = items.length > 100
    const hasHighMaxSelection = maxSelectable && maxSelectable > 7
    const needsVirtualization = items.length > 20 && !virtualize
    
    return showCognitiveWarning || hasHighItemCount || hasHighMaxSelection || needsVirtualization
  }, [items.length, maxSelectable, virtualize, showCognitiveWarning, cognitiveMode])

  // BIRHAUS: Auto-enable search for large lists
  const shouldAutoSearch = items.length > 20

  // BIRHAUS: Warn about max selections exceeding Miller's Law
  React.useEffect(() => {
    if (cognitiveMode === 'strict' && maxSelectable && maxSelectable > 7) {
      console.warn('BIRHAUS: maxSelectable > 7 may cause cognitive overload. Consider grouping or progressive disclosure.')
    }
  }, [maxSelectable, cognitiveMode])

  // Debounce search term
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [searchTerm, debounceMs])

  // Update selected keys when selectedItems prop changes
  React.useEffect(() => {
    setSelectedKeys(new Set(selectedItems.map(getItemKey)))
  }, [selectedItems, getItemKey])

  // Filter items based on search
  const filteredItems = React.useMemo(() => {
    if (!debouncedSearchTerm) return items

    const searchLower = debouncedSearchTerm.toLowerCase()
    
    return items.filter(item => {
      if (searchKeys.length === 0) {
        // Search all string properties if no keys specified
        return Object.values(item as any).some(value => 
          String(value).toLowerCase().includes(searchLower)
        )
      }
      
      return searchKeys.some(key => {
        const value = item[key]
        return String(value).toLowerCase().includes(searchLower)
      })
    })
  }, [items, debouncedSearchTerm, searchKeys])

  // Sort items if sortBy is provided
  const sortedItems = React.useMemo(() => {
    if (!sortBy) return filteredItems
    return [...filteredItems].sort(sortBy)
  }, [filteredItems, sortBy])

  // Group items if groupBy is provided
  const groupedItems = React.useMemo(() => {
    if (!groupBy) return null

    const groups = new Map<string, T[]>()
    
    sortedItems.forEach(item => {
      const group = groupBy(item)
      if (!groups.has(group)) {
        groups.set(group, [])
      }
      groups.get(group)!.push(item)
    })

    // BIRHAUS: Auto-expand if fewer than 7 groups
    if (cognitiveMode !== 'disabled' && groups.size <= 7 && expandedGroups.size === 0) {
      setExpandedGroups(new Set(groups.keys()))
    }

    return groups
  }, [sortedItems, groupBy, cognitiveMode, expandedGroups.size])

  // Get flat list of items for virtualization
  const flatItems = React.useMemo(() => {
    if (!groupedItems) return sortedItems

    const items: (T | { isGroup: true; name: string })[] = []
    
    groupedItems.forEach((groupItems, groupName) => {
      items.push({ isGroup: true, name: groupName } as any)
      if (expandedGroups.has(groupName) || !virtualize) {
        items.push(...groupItems)
      }
    })

    return items
  }, [sortedItems, groupedItems, expandedGroups, virtualize])

  // Virtual list setup
  const shouldVirtualizeList = virtualize && flatItems.length > 20
  const virtualizer = useVirtualizer({
    count: flatItems.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => itemHeight,
    overscan: 5,
    enabled: shouldVirtualizeList
  })

  const handleItemClick = (item: T) => {
    const key = getItemKey(item)

    if (multiSelect) {
      const newSelected = new Set(selectedKeys)
      
      if (newSelected.has(key)) {
        newSelected.delete(key)
      } else {
        if (maxSelectable && newSelected.size >= maxSelectable) {
          // BIRHAUS: Show cognitive warning instead of silent failure
          if (cognitiveMode === 'strict') {
            console.warn(`BIRHAUS: Maximum ${maxSelectable} selections reached`)
          }
          return
        }
        newSelected.add(key)
      }

      setSelectedKeys(newSelected)
      
      const selectedItemsList = items.filter(item => 
        newSelected.has(getItemKey(item))
      )
      onMultiSelect?.(selectedItemsList)
    } else {
      onSelect?.(item)
    }
  }

  const handleSelectAll = () => {
    if (selectedKeys.size === flatItems.filter(item => !(item as any).isGroup).length) {
      // Deselect all
      setSelectedKeys(new Set())
      onMultiSelect?.([])
    } else {
      // Select all (up to max)
      const allKeys = flatItems
        .filter(item => !(item as any).isGroup)
        .slice(0, maxSelectable)
        .map(item => getItemKey(item as T))
      
      setSelectedKeys(new Set(allKeys))
      
      const selectedItemsList = items.filter(item => 
        allKeys.includes(getItemKey(item))
      )
      onMultiSelect?.(selectedItemsList)
    }
  }

  const toggleGroup = (groupName: string) => {
    const newExpanded = new Set(expandedGroups)
    if (newExpanded.has(groupName)) {
      newExpanded.delete(groupName)
    } else {
      newExpanded.add(groupName)
    }
    setExpandedGroups(newExpanded)
  }

  const renderListItem = (item: T | { isGroup: true; name: string }, virtualRow?: any) => {
    if ((item as any).isGroup) {
      const groupName = (item as any).name
      const isExpanded = expandedGroups.has(groupName)
      
      return (
        <div
          key={groupName}
          className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200 px-4 py-2"
          style={virtualRow ? {
            height: `${virtualRow.size}px`,
            transform: `translateY(${virtualRow.start}px)`
          } : undefined}
        >
          <button
            onClick={() => toggleGroup(groupName)}
            className="flex items-center justify-between w-full text-left"
          >
            <span className="font-medium text-gray-700">{groupName}</span>
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      )
    }

    const key = getItemKey(item as T)
    const isSelected = selectedKeys.has(key)

    return (
      <motion.div
        key={key}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className={cn(
          'px-4 py-3 cursor-pointer transition-colors duration-150',
          'hover:bg-gray-50 border-b border-gray-100',
          isSelected && 'bg-blue-50 hover:bg-blue-100',
          virtualRow && 'absolute top-0 left-0 w-full'
        )}
        style={virtualRow ? {
          height: `${virtualRow.size}px`,
          transform: `translateY(${virtualRow.start}px)`
        } : undefined}
        onClick={() => handleItemClick(item as T)}
      >
        <div className="flex items-center gap-3">
          {multiSelect && (
            <BirhausCheckbox
              checked={isSelected}
              onChange={() => {}}
              className="pointer-events-none"
            />
          )}
          
          {renderItem ? (
            renderItem(item as T, isSelected)
          ) : (
            <div className="flex-1">
              <div className="font-medium text-gray-900">
                {String((item as any).name || (item as any).title || (item as any).label || key)}
              </div>
            </div>
          )}

          {!multiSelect && isSelected && (
            <Check className="w-5 h-5 text-blue-600 ml-auto" />
          )}
        </div>
      </motion.div>
    )
  }

  const selectedCount = selectedKeys.size
  const totalCount = flatItems.filter(item => !(item as any).isGroup).length
  const selectedCountText = selectedCountLabelEs
    .replace('{selected}', selectedCount.toString())
    .replace('{total}', totalCount.toString())

  return (
    <div className={cn('flex flex-col', className)}>
      {/* BIRHAUS: Cognitive Load Warning */}
      {shouldShowCognitiveWarning && cognitiveMode !== 'disabled' && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium mb-1">Advertencia de carga cognitiva</p>
            <p>
              Esta lista tiene {items.length} elementos. Para una experiencia óptima:
              {items.length > 100 && ' • Use la búsqueda para filtrar'}
              {maxSelectable && maxSelectable > 7 && ' • Limite las selecciones múltiples'}
              {!virtualize && items.length > 20 && ' • Habilite la virtualización'}
            </p>
          </div>
        </div>
      )}

      {/* Search Header */}
      <div className={cn('mb-4 space-y-3', searchClassName)}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={searchPlaceholderEs}
            className={cn(
              'w-full pl-10 pr-10 py-2.5 rounded-lg border border-gray-200',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
              'placeholder:text-gray-400 text-sm'
            )}
          />
          {searchTerm && (
            <button
              onClick={() => {
                setSearchTerm('')
                searchInputRef.current?.focus()
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Selection Controls */}
        {multiSelect && (showSelectedCount || showSelectAll) && (
          <div className="flex items-center justify-between text-sm">
            {showSelectedCount && (
              <span className="text-gray-600">
                {selectedCountText}
                {maxSelectable && ` (${maxSelectionLabelEs.replace('{max}', maxSelectable.toString())})`}
              </span>
            )}
            
            {showSelectAll && !maxSelectable && (
              <button
                onClick={handleSelectAll}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                {selectedKeys.size === totalCount
                  ? 'Deseleccionar todos'
                  : selectAllLabelEs}
              </button>
            )}
          </div>
        )}
      </div>

      {/* List Container */}
      <div
        ref={listRef}
        className={cn(
          'rounded-lg border border-gray-200 bg-white overflow-auto',
          listClassName
        )}
        style={{ height }}
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : flatItems.length === 0 ? (
          <BirhausEmptyState
            icon={<Users className="w-12 h-12" />}
            titleEs={searchTerm ? emptySearchMessageEs : emptyMessageEs}
            titleEn={searchTerm ? emptySearchMessageEn : emptyMessageEn}
            descriptionEs={searchTerm ? 'Intenta con otros términos de búsqueda' : undefined}
            descriptionEn={searchTerm ? 'Try different search terms' : undefined}
            className="py-12"
          />
        ) : shouldVirtualizeList ? (
          <div
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative'
            }}
          >
            {virtualizer.getVirtualItems().map(virtualRow => (
              renderListItem(flatItems[virtualRow.index], virtualRow)
            ))}
          </div>
        ) : (
          <AnimatePresence mode="popLayout">
            {flatItems.map(item => renderListItem(item))}
          </AnimatePresence>
        )}
      </div>

      {/* Selected Items Pills (BIRHAUS: Limited to 5 for cognitive load) */}
      {multiSelect && selectedKeys.size > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {Array.from(selectedKeys).slice(0, 5).map(key => {
            const item = items.find(i => getItemKey(i) === key)
            if (!item) return null
            
            return (
              <BirhausBadge
                key={key}
                variant="default"
                className="pl-2 pr-1 py-1"
              >
                <span className="text-xs">
                  {String((item as any).name || (item as any).title || (item as any).label || key)}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleItemClick(item)
                  }}
                  className="ml-1 hover:text-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </BirhausBadge>
            )
          })}
          {selectedKeys.size > 5 && (
            <BirhausBadge variant="default" className="text-xs">
              +{selectedKeys.size - 5} más
            </BirhausBadge>
          )}
        </div>
      )}
    </div>
  )
}